/*
 * Panelin görünüm tercihi: Grafit teması ve gece/gündüz modu.
 *
 * Panelin tek tasarımı yeni tasarımdır; klasik görünüm kullanıcı kararıyla
 * kaldırıldı (16.10.2026) ve eski `admin:design` kaydı artık okunmaz. Mod
 * yöneticinin kendi tarayıcısındadır (localStorage), sunucuya yazılmaz —
 * Vega'nın koyu/açık anahtarı ve Herkül'ün daraltılmış menüsüyle aynı
 * yaklaşım: bir yöneticinin seçimi başka bir yöneticinin ekranını değiştirmez.
 *
 * Tek tema Grafit'tir; Lacivert seçeneği kullanıcı kararıyla kaldırıldı
 * (08.10.2026) ve kayıtlı değer Grafit'e düşer. Değerler `<html>` üzerine
 * yazılır (`data-admin-design`, `-palette`, `-mode`): modallar portal ile
 * `document.body` altına taşındığı için `.admin-body` ağacının dışında
 * kalır ve tema değişkenlerini ancak kökten devralabilir. Stil kuralları
 * `data-admin-design="v2"` işaretine bağlı olduğu için işaret yazılmaya
 * devam eder.
 */
import { useCallback, useEffect, useState } from 'react';

export const PALETTE_KEY = 'admin:palette';
export const MODE_KEY = 'admin:mode';
export const DESIGN_EVENT = 'bhs:admin-design';

export const PALETTES = ['grafit'];
export const MODES = ['dark', 'light'];

const DEFAULTS = { design: 'v2', palette: 'grafit', mode: 'dark' };

const readKey = (key, allowed, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    // Gizli pencere ya da engellenmiş depolama: varsayılan görünüm.
    return fallback;
  }
};

const writeKey = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Kaydedilemezse tercih bu oturumla sınırlı kalır.
  }
};

export const readAdminDesign = () => ({
  design: DEFAULTS.design,
  palette: readKey(PALETTE_KEY, PALETTES, DEFAULTS.palette),
  mode: readKey(MODE_KEY, MODES, DEFAULTS.mode),
});

// Panelin yazı tipleri yalnızca panel açıldığında yüklenir; oyuncu portalı
// bu isteği hiç yapmaz.
const V2_FONTS_ID = 'admin-v2-fonts';
const V2_FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700'
  + '&family=Source+Sans+3:wght@400;500;600;700&display=swap';

const ensureV2Fonts = () => {
  if (document.getElementById(V2_FONTS_ID)) return;
  const link = document.createElement('link');
  link.id = V2_FONTS_ID;
  link.rel = 'stylesheet';
  link.href = V2_FONTS_HREF;
  document.head.appendChild(link);
};

/** Açık zeminli mi: Grafit'in gündüz modu. */
export const isLightSurface = (prefs) => prefs.mode === 'light';

/** Tercihi `<html>` üzerine yazar; `null` ile çağrılırsa izleri siler. */
export const applyAdminDesign = (prefs) => {
  const root = document.documentElement;
  if (!prefs) {
    delete root.dataset.adminDesign;
    delete root.dataset.adminPalette;
    delete root.dataset.adminMode;
    delete root.dataset.adminSurface;
    return;
  }
  root.dataset.adminDesign = prefs.design;
  root.dataset.adminPalette = prefs.palette;
  root.dataset.adminMode = prefs.mode;
  root.dataset.adminSurface = isLightSurface(prefs) ? 'light' : 'dark';
  if (prefs.design === 'v2') ensureV2Fonts();
};

/** Tercihi okur ve değiştirir; aynı sayfadaki başka bir bileşen olayla güncel kalır. */
export const useAdminDesign = () => {
  const [prefs, setPrefs] = useState(readAdminDesign);

  useEffect(() => {
    const sync = () => setPrefs(readAdminDesign());
    window.addEventListener(DESIGN_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(DESIGN_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const update = useCallback((patch) => {
    const next = { ...readAdminDesign(), ...patch, design: DEFAULTS.design };
    writeKey(PALETTE_KEY, next.palette);
    writeKey(MODE_KEY, next.mode);
    setPrefs(next);
    window.dispatchEvent(new Event(DESIGN_EVENT));
  }, []);

  return { ...prefs, update };
};
